import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import { Flex } from '@mantine/core';
import { AxiosError } from 'axios';
import {
  Button,
  Colors,
  Icon,
  IconName,
  SizesButton,
  Text,
  TypesButton,
  Typography,
} from 'goalsetter-webapp-components';
import GoalCard from 'assets/img/goalCard';
import { useMediaQueryGs } from 'app/hooks/useMediaQuery';
import useFetchData from 'app/hooks/useFetchData';
import UserItem from 'app/components/UserItem';
import {
  Container,
  GoalCardBackground,
  IconWrapper,
  MobileTitleContainer,
  StyledCard,
  StyledInput,
  StyledTitleContainer,
  StyledTitleLeftCol,
  StyledTitleRightCol,
  inviteButtonStyle,
  inviteLabelStyle,
  inviteLabelTextStyle,
  mainTitleStyle,
  paddingLeft,
  subtitleStyle,
  titleContainerStyle,
  titleStyle,
} from './styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { FamilyMember } from 'types/goals';
import userService from 'api/userApi';
import { RoutesGs } from '../../../../router/RoutesList';
import { Trans } from 'react-i18next';
import LogoHeader from 'app/components/LogoHeader';
import { sendAnalyticsEvent } from '../../../../utils/analytics/analyticsWrapper';
import { ActionType, CategoryType } from '../../../../types/analytics/enum';
import SimpleModal from 'app/components/Modals/SimpleModal';

export const SendGift: React.FC = () => {
  const [name, setName] = useState<string>('');
  const [showNotAllowedModal, setShowNotAllowedModal] =
    useState<boolean>(false);
  const [emptyResponse, setEmptyResponse] = useState<boolean>(false);
  const { isMobile } = useMediaQueryGs();
  const navigate = useNavigate();
  const { state } = useLocation();
  const userNotFound = state?.userNotFound as boolean;

  const { data, loading, fetchData } = useFetchData<
    FamilyMember[],
    AxiosError
  >();
  const userFound = data && data[0];
  const goToSelectCard = () => {
    if (userFound && !userFound.canReceiveMoney) {
      return setShowNotAllowedModal(true);
    }
    sendAnalyticsEvent(
      CategoryType.customizeGoalcardContinue,
      ActionType.Goalcard_CustomizeGoalcard_ContinueButton,
    );
    navigate(RoutesGs.SelectGoalcard, {
      state: {
        selectedUser: userFound,
      },
    });
  };

  const handleCloseNotAllowedModal = () => setShowNotAllowedModal(false);

  const goToInvite = () => {
    sendAnalyticsEvent(
      CategoryType.searchProfileContinue,
      ActionType.Goalcard_SearchProfile_ContinueButton,
    );
    if (isMobile && navigator.share) {
      navigator.share({
        title: String(t('sendInvitation.share.title')),
        text: String(t('sendInvitation.share.text')),
        url: String(process.env.REACT_APP_DOWNLOAD_APP_LINK),
      });
      return;
    }
    navigate(RoutesGs.SendInvitation);
    return;
  };

  const handleSearch = () => {
    if (!name) return;
    fetchData(userService.getPublicInformationByUsername(name));
  };

  const handleKeyDown = (e: { key: string }) => {
    if (e.key === 'Enter' && name.trim() !== '') {
      fetchData(userService.getPublicInformationByUsername(name));
    }
    return;
  };

  useEffect(() => {
    // Remove error styles when user starts typing again
    setEmptyResponse(false);
  }, [name]);

  useEffect(() => {
    if (data && data?.length === 0) {
      sendAnalyticsEvent(
        CategoryType.searchProfileNotMatched,
        ActionType.Goalcard_SearchProfile_ProfileNotMatched,
      );
      setEmptyResponse(true);
      return;
    }
    if (data && data[0]) {
      sendAnalyticsEvent(
        CategoryType.searchProfileMatched,
        ActionType.Goalcard_SearchProfile_ProfileMatched,
      );
    }
    setEmptyResponse(false);
  }, [data, loading]);

  const MobileTitle = () => {
    return (
      <MobileTitleContainer>
        <Trans
          style={mainTitleStyle}
          i18nKey={t('sendGift.mainTitle')}
          values={{
            reg: '®',
          }}
          components={{
            parent: (
              <Text
                type={Typography.GSH4}
                color={Colors.WHITE}
                style={mainTitleStyle}
              >
                {''}
              </Text>
            ),
            span: (
              <Text
                type={Typography.GSH6}
                color={Colors.WHITE}
                style={mainTitleStyle}
              >
                {''}
              </Text>
            ),
          }}
        />
      </MobileTitleContainer>
    );
  };

  const DesktopTitle = () => {
    return (
      <StyledTitleContainer>
        <StyledTitleLeftCol>
          <Trans
            i18nKey={t('sendGift.mainTitleDesktopFirst')}
            values={{
              reg: '®',
            }}
            components={{
              parent: (
                <Text
                  type={Typography.GSH1}
                  color={Colors.WHITE}
                  style={mainTitleStyle}
                >
                  {''}
                </Text>
              ),
              span: (
                <Text
                  type={Typography.GSH5}
                  color={Colors.WHITE}
                >
                  {''}
                </Text>
              ),
            }}
          />
          <br></br>
          <Trans
            i18nKey={t('sendGift.mainTitleDesktopSecond')}
            values={{
              reg: '®',
            }}
            components={{
              parent: (
                <Text
                  type={Typography.GSH2}
                  color={Colors.WHITE}
                  style={mainTitleStyle}
                >
                  {''}
                </Text>
              ),
              span: (
                <Text
                  type={Typography.GSH5}
                  color={Colors.WHITE}
                >
                  {''}
                </Text>
              ),
            }}
          />
        </StyledTitleLeftCol>
        <StyledTitleRightCol>
          <GoalCard data-testid="imageGoalCard" />
        </StyledTitleRightCol>
      </StyledTitleContainer>
    );
  };

  const RenderTitle = () => {
    if (isMobile) {
      return <MobileTitle />;
    } else {
      return <DesktopTitle />;
    }
  };

  return (
    <GoalCardBackground>
      <Container>
        <LogoHeader />
        {userNotFound && (
          <StyledCard
            fullWidth
            marginTop
          >
            <IconWrapper>
              <Icon
                icon={IconName.ATTENTION}
                size={48}
                color={Colors.WHITE}
              />
            </IconWrapper>
            <Flex
              direction="column"
              style={paddingLeft}
            >
              <Text
                color={Colors.WHITE}
                type={Typography.GSBODY3}
              >
                {t('sendGift.userProfileNotFound.title')}
              </Text>
              <Text
                color={Colors.WHITE}
                type={Typography.GSCAPTION}
              >
                {t('sendGift.userProfileNotFound.subtitle')}
              </Text>
            </Flex>
          </StyledCard>
        )}
        <RenderTitle />
        <Flex
          direction="column"
          justify="flex-start"
          style={titleContainerStyle}
        >
          <Text
            type={Typography.GSH5}
            color={Colors.WHITE}
            style={titleStyle(isMobile)}
          >
            {t('sendGift.title')}
          </Text>
          <Text
            type={Typography.GSBODY3}
            color={Colors.WHITE}
            style={subtitleStyle(isMobile)}
          >
            {t('sendGift.subtitle')}
          </Text>
          <StyledInput
            placeholder={t('sendGift.input.placeholder')}
            onChangeValue={setName}
            value={name}
            error={emptyResponse ? 'Error' : undefined}
            rightIcon={IconName.SEARCH}
            onRightIconClick={handleSearch}
            testId="inputProfileName"
            isLoading={loading}
            onKeyDown={handleKeyDown}
          />
          {userFound && (
            <UserItem
              profileImageUrl={userFound.profileImageUrl}
              firstName={userFound.firstname}
              userName={userFound.userName}
              color={Colors.WHITE}
              onInvite={goToSelectCard}
            />
          )}
          {emptyResponse && (
            <StyledCard>
              <Text
                color={Colors.WHITE}
                type={Typography.GSCAPTION}
              >
                {t('sendGift.notFound')}
              </Text>
            </StyledCard>
          )}
          <Flex
            justify="space-between"
            align="center"
            style={inviteLabelStyle}
          >
            <Text
              type={Typography.GSSUBTITLE1}
              color={Colors.WHITE}
              style={inviteLabelTextStyle(isMobile)}
            >
              {t('sendGift.inviteLabel')}
            </Text>
            <Button
              size={SizesButton.EXTRA_SMALL}
              type={TypesButton.WHITE_SECONDARY}
              onClick={goToInvite}
              style={inviteButtonStyle}
            >
              {t('sendGift.inviteButtonLabel')}
            </Button>
          </Flex>
        </Flex>
      </Container>
      <SimpleModal
        show={showNotAllowedModal}
        onClose={handleCloseNotAllowedModal}
        title={String(t('userNotAbleToReceiveMoneyModal.title'))}
        description={String(t('userNotAbleToReceiveMoneyModal.subtitle'))}
      />
    </GoalCardBackground>
  );
};
